// ════════════════════════════════════════════════════════════════════════════
//  Regras de aprovação de transferência (WMS) — CRUD
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, ShieldCheck, Pencil, Trash2 } from 'lucide-react';
import { getApiErrorMessage } from '@/lib/apiClient';
import { transferenciaService as svc, TIPOS_GATILHO, type RegraAprovacao, type SalvarRegraRequest } from '@/features/wms/wmsService';
import { useLocalEstoqueCombo } from '@/features/estoque/hooks/useLocalEstoque';
import { ProdutoSearchCombobox } from '@/components/ProdutoSearchCombobox';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const SENT = '__none__';

export default function RegrasAprovacaoTransferencia() {
  const qc = useQueryClient();
  const { data: regras = [] } = useQuery({ queryKey: ['regras-transferencia'], queryFn: () => svc.listarRegras() });
  const { data: locais = [] } = useLocalEstoqueCombo();
  const [editing, setEditing] = useState<RegraAprovacao | 'new' | null>(null);

  const inval = () => qc.invalidateQueries({ queryKey: ['regras-transferencia'] });
  const excluir = useMutation({
    mutationFn: (id: number) => svc.excluirRegra(id),
    onSuccess: () => { toast.success('Regra excluída'); inval(); }, onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Regras de Aprovação de Transferência</h1>
          <p className="text-muted-foreground mt-1">Sem regras ativas, a transferência segue direto p/ despacho. Com regras (OR), exige aprovação.</p>
        </div>
        <Button className="gap-2" onClick={() => setEditing('new')}><Plus className="h-4 w-4" />Nova Regra</Button>
      </div>

      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5" />Regras</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Descrição</TableHead><TableHead>Gatilho</TableHead><TableHead>Critério</TableHead>
              <TableHead>Ativa</TableHead><TableHead className="text-right">Ações</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {!regras.length ? (
                <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Nenhuma regra cadastrada</TableCell></TableRow>
              ) : regras.map(r => (
                <TableRow key={r.id}>
                  <TableCell className="font-medium">{r.descricao}</TableCell>
                  <TableCell>{r.tipoGatilho}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {r.tipoGatilhoId === 1 && `≥ R$ ${r.valorMinimo?.toLocaleString('pt-BR')}`}
                    {r.tipoGatilhoId === 2 && `Produto #${r.produtoId}`}
                    {r.tipoGatilhoId === 3 && `${r.localOrigemId} → ${r.localDestinoId}`}
                    {r.tipoGatilhoId === 0 && '—'}
                  </TableCell>
                  <TableCell>{r.isAtivo ? <Badge className="bg-green-500/10 text-green-600">Ativa</Badge> : <Badge variant="secondary">Inativa</Badge>}</TableCell>
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" onClick={() => setEditing(r)}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => excluir.mutate(r.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {editing && (
        <RegraDialog regra={editing === 'new' ? null : editing} locais={locais}
          onClose={() => setEditing(null)} onSaved={() => { setEditing(null); inval(); }} />
      )}
    </div>
  );
}

function RegraDialog({ regra, locais, onClose, onSaved }: {
  regra: RegraAprovacao | null; locais: { id: number; descricao: string }[];
  onClose: () => void; onSaved: () => void;
}) {
  const [form, setForm] = useState<SalvarRegraRequest>({
    descricao: regra?.descricao ?? '', tipoGatilho: regra?.tipoGatilhoId ?? 0,
    valorMinimo: regra?.valorMinimo ?? null, produtoId: regra?.produtoId ?? null,
    localOrigemId: regra?.localOrigemId ?? null, localDestinoId: regra?.localDestinoId ?? null,
    isAtivo: regra?.isAtivo ?? true,
  });
  const salvar = useMutation({
    mutationFn: () => regra ? svc.atualizarRegra(regra.id, form) : svc.criarRegra(form),
    onSuccess: () => { toast.success('Regra salva'); onSaved(); }, onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const set = (p: Partial<SalvarRegraRequest>) => setForm({ ...form, ...p });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{regra ? 'Editar' : 'Nova'} Regra</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>Descrição</Label><Input value={form.descricao} onChange={e => set({ descricao: e.target.value })} /></div>
          <div><Label>Gatilho</Label>
            <Select value={String(form.tipoGatilho)} onValueChange={(v) => set({ tipoGatilho: Number(v) })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{TIPOS_GATILHO.map(g => <SelectItem key={g.id} value={String(g.id)}>{g.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {form.tipoGatilho === 1 && (
            <div><Label>Valor mínimo (R$)</Label><Input type="number" value={form.valorMinimo ?? ''} onChange={e => set({ valorMinimo: Number(e.target.value) })} /></div>
          )}
          {form.tipoGatilho === 2 && (
            <div><Label>Produto sensível</Label><ProdutoSearchCombobox value={form.produtoId ?? undefined} onChange={(v) => set({ produtoId: v })} /></div>
          )}
          {form.tipoGatilho === 3 && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Origem</Label>
                <Select value={form.localOrigemId ? String(form.localOrigemId) : SENT} onValueChange={(v) => set({ localOrigemId: v === SENT ? null : Number(v) })}>
                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{locais.map(l => <SelectItem key={l.id} value={String(l.id)}>{l.descricao}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label>Destino</Label>
                <Select value={form.localDestinoId ? String(form.localDestinoId) : SENT} onValueChange={(v) => set({ localDestinoId: v === SENT ? null : Number(v) })}>
                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{locais.map(l => <SelectItem key={l.id} value={String(l.id)}>{l.descricao}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
          )}
          <div className="flex items-center gap-2"><Switch checked={form.isAtivo} onCheckedChange={(c) => set({ isAtivo: c })} /><Label>Ativa</Label></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button disabled={!form.descricao || salvar.isPending} onClick={() => salvar.mutate()}>Salvar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
